import { Link } from "react-router-dom";
import { RotateCcw, Settings2, Maximize, Terminal } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../ui/alert-dialog";

export const toggleFullscreen = () => {
  if (document.fullscreenElement) document.exitFullscreen();
  else document.documentElement.requestFullscreen?.();
};

const audioText = (a) => {
  if (!a || a.status === "idle") return "AUDIO · CHECKING";
  if (a.status === "ready") return `AUDIO ${a.total}/${a.total} · STORED ON THIS MACHINE`;
  if (a.status === "retry") return `AUDIO ${a.done}/${a.total} · ${a.failed} MISSING · RETRYING`;
  return `AUDIO ${a.done}/${a.total} · DOWNLOADING`;
};

export const LabHeader = ({ title, decoded, total, sealed, audio, linkDown, onReset, onOperator }) => (
  <header className="flex flex-wrap items-center justify-between gap-4 border-b border-cyan-400/20 pb-5">
    <div>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span data-testid="brand" className="brand text-xl sm:text-2xl">The Spore Project</span>
        <span className="lab-label">Research &amp; Containment Site · 123 Grey's Inn Road, London</span>
      </div>
      <p className="lab-label mt-3 !text-cyan-300">Spore Lab</p>
      <h1 data-testid="kiosk-title" className="spore-headline mt-1 text-3xl sm:text-4xl lg:text-5xl">
        {title}
      </h1>
    </div>
    <div className="flex flex-col items-end gap-2">
      <div className="flex items-center gap-3">
      <div data-testid="specimen-count-badge" className="font-mono text-sm sm:text-base tracking-widest border border-emerald-400/40 bg-emerald-400/5 px-4 py-2 text-emerald-300">
        <span className="text-emerald-200 text-lg">{String(decoded).padStart(2, "0")}</span> / {String(total).padStart(2, "0")} MEMORIES RECOVERED
      </div>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button data-testid="reset-session-button" className="lab-icon-btn" title="Reset to standby (Esc)">
            <RotateCcw size={18} />
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent className="lab-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl uppercase tracking-widest">End this run?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              The case file is wiped so the next group of research assistants starts from the briefing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-cancel-button" className="lab-btn-ghost">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="reset-confirm-button" className="lab-btn-danger" onClick={onReset}>
              End &amp; restart
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <button data-testid="fullscreen-button" className="lab-icon-btn" onClick={toggleFullscreen} title="Full screen (F)">
        <Maximize size={18} />
      </button>
      <button data-testid="operator-button" className="lab-icon-btn" onClick={onOperator} title="Operator menu (`)">
        <Terminal size={18} />
      </button>
      {!sealed && (
        <Link data-testid="admin-login-button" to="/setup" className="lab-icon-btn" title="Programming bench">
          <Settings2 size={18} />
        </Link>
      )}
      </div>
      <p data-testid="link-status" data-audio={audio?.status} data-link={linkDown ? "down" : "ok"} className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-slate-300">
        <span className={`inline-block h-2 w-2 rounded-full ${audio?.status === "ready" ? "bg-emerald-300" : audio?.status === "retry" ? "bg-amber-300" : "bg-cyan-300 animate-pulse"}`} />
        <span data-testid="audio-cache-status">{audioText(audio)}</span>
        <span className="text-slate-500">|</span>
        <span data-testid="link-state" className={linkDown ? "text-amber-300" : "text-emerald-300/80"}>{linkDown ? "LINK DOWN · RUNNING FROM LOCAL STORE" : "LINK OK"}</span>
      </p>
    </div>
  </header>
);
